import { ChangeEvent, FormEvent, useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { WeightParameters } from '@/enums/bodyParameters'
import { Buttons } from '@/enums/buttons'
import { PageName } from '@/enums/pageName'
import { validWeightValue } from '@/utils/weightUtils'

import { PrimaryButton, ButtonSpinner } from '@/components/buttons/primaryButton'
import { MeasurementSection } from '@/components/measurementSection'
import { PageTitle } from '@/components/pageTitle'
import { FooterPolicy } from '@/components/policyLinkBlock'
import { RecommendedWeight } from '@/components/recommendedWeight'
import { TextBlock } from '@/components/textBlock'
import { TextHTML } from '@/components/typography'
import { WeightSuccessMessage } from '@/components/weightSuccessMessage'

import { useAuth } from '@/auth/useAuth'
import { useSession } from '@/core/session/useSession'
import { trackPageView, trackPageComplete } from '@/events/events'

import { TBodyParamsProps } from '../heightPage/types'
import { MeasurementInput } from '../pageComponents'
import { convertLbsToKg } from '../utils'

export const WeightPage = ({
  pageName,
  pageConfig,
  setLastVisitedItem,
  getItem,
  setItem,
  config,
}: TBodyParamsProps) => {
  const { user } = useAuth()
  const sessionContext = useSession()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [measurementSystem, setMeasurementSystem] = useState<WeightParameters>(WeightParameters.LBS)
  const [error, setError] = useState<string | null>(null)
  const isWeightGoalPage = pageName === PageName.weightGoal

  const {
    title,
    pageDescription,
    nextUrl,
    tabTitle,
    buttonName,
    policyBlock = false,
    isShowDescription = true,
    textBlocksF,
    textBlocks,
    validateBMIRefValue = false,
  } = pageConfig
  const isFT = measurementSystem === WeightParameters.LBS

  const [value, setValue] = useState<string>('')
  const navigate = useNavigate()

  useEffect(() => {
    const getWeightDataFromStorage = getItem(pageName) || ''
    const weightData = (getWeightDataFromStorage && JSON.parse(getWeightDataFromStorage)) || {}
    const weightMeasurement =
      weightData?.measurementSystem === WeightParameters.KG ? 'metric' : 'imperial'

    const prevPageData = getItem(PageName.height) || ''
    const convertPrevPageDataToObject = (prevPageData && JSON.parse(prevPageData)) || ''
    const measurementType = convertPrevPageDataToObject?.type
    if (weightMeasurement === measurementType) {
      setValue(weightData?.value || '')
    }
    if (measurementType === 'imperial') {
      setMeasurementSystem(WeightParameters.LBS)
    } else {
      setMeasurementSystem(WeightParameters.KG)
    }
  }, [pageName, getItem, measurementSystem])

  useEffect(
    () => {
      document.title = tabTitle ? tabTitle : title
      trackPageView(
        pageName,
        config.PARTNER_ID,
        config.APP_ID,
        config.PAGE_SET_ID,
        sessionContext,
        user
      )
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  const changeMeasurement = useCallback(
    (value: WeightParameters) => () => {
      setMeasurementSystem(value)
      setValue('')
    },
    []
  )

  const changeValue = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setValue(event.currentTarget.value)

      const result = validWeightValue(
        measurementSystem,
        event.currentTarget.value,
        pageName,
        getItem,
        validateBMIRefValue
      )

      if (pageName === PageName.weight && result) {
        setError(result)
        return
      }
      if (pageName === PageName.weightGoal && result) {
        setError(result)
        return
      }

      setError(null)
    },
    [measurementSystem, pageName, getItem, validateBMIRefValue]
  )

  const nextPage = async (e?: FormEvent) => {
    if (e) {
      e.preventDefault()
      e.stopPropagation()
    }
    setIsLoading(true)

    const answerInKg = measurementSystem == WeightParameters.LBS ? convertLbsToKg(+value) : value
    trackPageComplete(
      pageName,
      [answerInKg.toString()],
      config.PARTNER_ID,
      config.APP_ID,
      config.PAGE_SET_ID,
      sessionContext,
      user
    )

    navigate(nextUrl)
    const payload = {
      value: value,
      measurementSystem: measurementSystem,
    }
    setItem(pageName, `${JSON.stringify(payload)}`)
    setLastVisitedItem(pageName)
  }

  const className = `page page-survey page-measurements page-${pageName}`
  return (
    <div className={className}>
      <PageTitle title={title} />
      {pageDescription && <TextHTML className="page-description" title={pageDescription} />}
      <MeasurementSection
        changeLeftMeasurement={changeMeasurement(WeightParameters.KG)}
        changeRightMeasurement={changeMeasurement(WeightParameters.LBS)}
        isActiveLeft={!isFT}
        leftName={WeightParameters.KG}
        rightName={WeightParameters.LBS}
      />
      <form onSubmit={nextPage}>
        <MeasurementInput
          maxLength={3}
          size={1}
          type={'number'}
          value={value}
          onChange={changeValue}
          unit={measurementSystem}
          disabled={isLoading}
        />
      </form>

      {isShowDescription && (
        <>
          {!error && value && <WeightSuccessMessage pageName={pageName} />}
          {isWeightGoalPage && <RecommendedWeight getItem={getItem} />}
          {error && <div className="error-message" dangerouslySetInnerHTML={{ __html: error }} />}
        </>
      )}
      {value &&
        (textBlocks || (textBlocksF && textBlocksF(value, measurementSystem)) || []).map(
          ({ text, ...blockProps }) => {
            const inputText = typeof text === 'function' ? text() : text
            if (inputText === '') {
              return inputText
            }
            return <TextBlock key={inputText} text={inputText} {...blockProps} />
          }
        )}

      <div className="button-container">
        <PrimaryButton onClick={nextPage} disabled={isLoading || !value || Boolean(error)}>
          {isLoading ? <ButtonSpinner /> : buttonName || Buttons.NEXT}
        </PrimaryButton>
      </div>
      {policyBlock && <FooterPolicy />}
    </div>
  )
}
